/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Input, NgZone, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import * as _ from 'lodash';
import {finalize, Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import template from './third-party-audience-upload-modal.component.html';
import {APP_SHARED} from '../common/app-shared';
import {AdnCountrySelectComponent} from '../common/directives/country-select/adn-country-select.component';
import {ThirdPartyAudienceApiService} from './third-party-audience-api.service';

const DEFAULT_PROVIDER = 'LC_MEDIA';

interface UploadModalConfig {
  title: string;
  instructions: string;
  columns: string[];
  examples: string[];
}

const UPLOAD_MODAL_CONFIG: Record<'data' | 'definitions', UploadModalConfig> = {
  data: {
    title: 'Upload Audience Data',
    instructions: 'Upload a CSV file that assigns postcode data to existing third-party audiences. The first column is the audience reference code, and a variable number of columns are used for the postcodes',
    columns: ['Audience external reference', 'Postcode 1', 'Postcode 2', '...', 'Postcode N'],
    examples: [
      'audience_ref_123,0001,0430,0124',
      'audience_ref_456,0005',
      'audience_ref_789,0099,0657,006,0700'
    ]
  },
  definitions: {
    title: 'Batch Upload Audience Definitions',
    instructions: 'Upload a CSV file that creates or updates third-party audience definitions.',
    columns: ['Audience external reference', 'Audience name', 'Description', 'Teams (optional)'],
    examples: [
      'audience_ref_123,High Income,People that live in high income houses,team_abc',
      'audience_ref_456,Medium Income,People that live in medium income houses,',
      'audience_ref_789,Low Income,People that live in low income houses,team_xyz,team_abc'
    ]
  }
};

@Component({
  standalone: true,
  imports: [...APP_SHARED, FormsModule, AdnCountrySelectComponent],
  selector: 'adn-third-party-audience-upload-modal',
  template
})
export class ThirdPartyAudienceUploadModalComponent implements OnInit {
  @Input() uploadType: 'data' | 'definitions' = 'definitions';

  readonly onClosed = new Subject<any>();

  readonly providers = ['NORDIC_DATA_RESOURCES', 'LC_MEDIA'];

  title = '';
  instructions = '';
  columns: string[] = [];
  examplesText = '';

  uploadParams: {provider: string; countryIsoCode?: string} = {
    provider: DEFAULT_PROVIDER
  };

  private readonly progressState = signal(0);
  private readonly validationErrorsState = signal<any[]>([]);
  private readonly uploadErrorState = signal<string | null>(null);
  private readonly resultState = signal<any>(null);
  private readonly uploadedCountState = signal(0);
  private readonly receivedCountState = signal(0);
  private readonly fileNameState = signal('');
  private readonly uploadingState = signal(false);

  get progress(): number { return this.progressState(); }
  set progress(value: number) { this.progressState.set(value); }
  get validationErrors(): any[] { return this.validationErrorsState(); }
  set validationErrors(value: any[]) { this.validationErrorsState.set(value); }
  get uploadError(): string | null { return this.uploadErrorState(); }
  set uploadError(value: string | null) { this.uploadErrorState.set(value); }
  get result(): any { return this.resultState(); }
  set result(value: any) { this.resultState.set(value); }
  get uploadedCount(): number { return this.uploadedCountState(); }
  set uploadedCount(value: number) { this.uploadedCountState.set(value); }
  get receivedCount(): number { return this.receivedCountState(); }
  set receivedCount(value: number) { this.receivedCountState.set(value); }
  get fileName(): string { return this.fileNameState(); }
  set fileName(value: string) { this.fileNameState.set(value); }
  get uploading(): boolean { return this.uploadingState(); }
  set uploading(value: boolean) { this.uploadingState.set(value); }

  constructor(
    public bsModalRef: BsModalRef,
    private readonly api: ThirdPartyAudienceApiService,
    private readonly ngZone: NgZone
  ) {
  }

  ngOnInit(): void {
    const config = UPLOAD_MODAL_CONFIG[this.uploadType];
    this.title = config.title;
    this.instructions = config.instructions;
    this.columns = config.columns;
    this.examplesText = config.examples.join('\n');

    if (this.isDataUpload) {
      this.uploadParams.countryIsoCode = 'NO';
    }
  }

  get isDataUpload(): boolean {
    return this.uploadType === 'data';
  }

  canUpload(): boolean {
    return !!this.uploadParams.provider && (!this.isDataUpload || !!this.uploadParams.countryIsoCode);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.canUpload()) {
      return;
    }
    this.upload(file);
  }

  upload(file: File): void {
    this.fileName = file.name;
    this.progress = 0;
    this.result = null;
    this.uploadError = null;
    this.validationErrors = [];
    this.uploading = true;

    const onProgress = (progressEvent: any) => {
      if (progressEvent?.total) {
        this.ngZone.run(() => {
          this.progress = Math.min(100, parseInt(String(100 * progressEvent.loaded / progressEvent.total), 10));
        });
      }
    };
    const upload = this.isDataUpload ?
      this.api.uploadAudienceData(
        file,
        this.uploadParams.provider,
        this.uploadParams.countryIsoCode as string,
        onProgress
      ) :
      this.api.uploadAudienceDefinitions(file, this.uploadParams.provider, onProgress);

    upload.pipe(finalize(() => {
      this.ngZone.run(() => {
        this.uploading = false;
      });
    })).subscribe({
      next: (response) => {
        this.ngZone.run(() => {
          this.result = response;
          this.uploadedCount = _.get(this.result, ['parameters', 'uploaded'], 0);
          this.receivedCount = _.get(this.result, ['parameters', 'received'], 0);
          this.progress = 100;
        });
      },
      error: (error) => {
        this.ngZone.run(() => {
          this.validationErrors = _.get(error, ['data', 'errors'], []);
          if (!this.validationErrors.length) {
            this.uploadError = _.get(error, ['data', 'message']) || 'The file could not be uploaded.';
          }
        });
      }
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
    this.onClosed.next(this.result);
  }
}
